import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  bootstrapMe,
  getGroupMembers,
  GroupDto,
  MeDto,
  PersonDto,
} from "./api";
import {
  captureInviteFromUrl,
  clearInviteToken,
  getInviteToken,
} from "./utils/inviteToken";

const ACTIVE_GROUP_KEY = "p2pActiveGroupId";

export interface CurrentUserContextType {
  personId: number | null;
  name: string | null;
  groups: GroupDto[];
  activeGroup: GroupDto | null;
  activeGroupId: number | null;
  members: PersonDto[];
  /** Gruppe, zu der {@link members} aktuell gehören (für Lade-Gating). */
  membersGroupId: number | null;
  isAdmin: boolean;
  /** Darf teure Endpunkte (OCR/KI/orderRewe …) nutzen (Freischalt-Gruppe). */
  expensive: boolean;
  loading: boolean;
  setActiveGroupId: (id: number) => void;
  refresh: () => void;
}

export const CurrentUserContext = createContext<CurrentUserContextType>({
  personId: null,
  name: null,
  groups: [],
  activeGroup: null,
  activeGroupId: null,
  members: [],
  membersGroupId: null,
  isAdmin: false,
  expensive: false,
  loading: false,
  setActiveGroupId: () => {},
  refresh: () => {},
});

function readInviteToken(): string | undefined {
  // Token kann aus der aktuellen URL stammen (falls bereits eingeloggt) oder
  // zuvor vor dem Login-Redirect in localStorage gesichert worden sein.
  captureInviteFromUrl();
  return getInviteToken();
}

export const CurrentUserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [personId, setPersonId] = useState<number | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [groups, setGroups] = useState<GroupDto[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [expensive, setExpensive] = useState(false);
  const [activeGroupId, setActiveGroupIdState] = useState<number | null>(null);
  const [members, setMembers] = useState<PersonDto[]>([]);
  const [membersGroupId, setMembersGroupId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const applyMe = useCallback((me: MeDto) => {
    setPersonId(me.personId ?? null);
    setName(me.name ?? null);
    const meGroups = me.groups ?? [];
    setGroups(meGroups);
    setIsAdmin(me.admin ?? false);
    setExpensive(me.expensive ?? false);
    const stored = Number(localStorage.getItem(ACTIVE_GROUP_KEY));
    const storedGroup = meGroups.find((group) => group.id === stored);
    const shared = meGroups.find((group) => !group.personal);
    const personal = meGroups.find((group) => group.personal);
    const next =
      storedGroup?.id ?? shared?.id ?? personal?.id ?? meGroups[0]?.id ?? null;
    setActiveGroupIdState(next);
    if (next != null) {
      localStorage.setItem(ACTIVE_GROUP_KEY, String(next));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const inviteToken = readInviteToken();
        const me = (await bootstrapMe({ body: { inviteToken } })).data;
        if (!cancelled) {
          applyMe(me);
          clearInviteToken();
          if (window.location.pathname.startsWith("/join/")) {
            window.history.replaceState(null, "", "/");
          }
        }
      } catch (error) {
        console.error("Bootstrap /me fehlgeschlagen", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyMe, reloadKey]);

  useEffect(() => {
    if (activeGroupId == null) {
      setMembers([]);
      setMembersGroupId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = (
          await getGroupMembers({ path: { groupId: activeGroupId } })
        ).data;
        if (!cancelled) {
          setMembers(result);
          setMembersGroupId(activeGroupId);
        }
      } catch (error) {
        console.error("Mitglieder laden fehlgeschlagen", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeGroupId, reloadKey]);

  const setActiveGroupId = useCallback((id: number) => {
    setActiveGroupIdState(id);
    localStorage.setItem(ACTIVE_GROUP_KEY, String(id));
  }, []);

  const activeGroup = useMemo(
    () => groups.find((group) => group.id === activeGroupId) ?? null,
    [groups, activeGroupId],
  );

  const value = useMemo<CurrentUserContextType>(
    () => ({
      personId,
      name,
      groups,
      activeGroup,
      activeGroupId,
      members,
      membersGroupId,
      isAdmin,
      expensive,
      loading,
      setActiveGroupId,
      refresh: () => setReloadKey((key) => key + 1),
    }),
    [
      personId,
      name,
      groups,
      activeGroup,
      activeGroupId,
      members,
      membersGroupId,
      isAdmin,
      expensive,
      loading,
      setActiveGroupId,
    ],
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};

export const useCurrentUser = (): CurrentUserContextType =>
  useContext(CurrentUserContext);
